import React from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { Link } from 'react-router-dom';
import {
  Flag, Users, Car, Route, Eye, Gauge, ShieldAlert, BookOpen, ArrowRight, Quote,
} from 'lucide-react';

const KEY_STATS = [
  { n: '3 000 km', l: 'minimum en conduite accompagnée, sur ≥ 1 an' },
  { n: '17 ans', l: "examen pratique ET conduite seul (loi 2024)" },
  { n: '6 → 12 pts', l: 'permis probatoire raccourci à 2 ans (+3 pts/an)' },
  { n: '500 km', l: "le cap de l'excès de confiance — reste vigilant" },
  { n: '1 seconde', l: 'de temps de réaction avant même de freiner' },
  { n: '× 15', l: "risque mortel avec alcool + cannabis combinés" },
];

const SECTIONS = [
  {
    icon: Flag,
    title: 'Ton parcours AAC en 4 temps',
    items: [
      'Formation initiale en auto-école : code + au moins 20 h de conduite',
      'Rendez-vous préalable (2 h) avec ton accompagnateur : remise du guide et de l\'attestation AFFI',
      'Conduite accompagnée : ≥ 1 an et ≥ 3 000 km, avec 2 rendez-vous pédagogiques obligatoires (≈ 1 000 km puis ≥ 3 000 km)',
      'Examen pratique dès 17 ans — et conduite seul dès 17 ans depuis 2024',
    ],
  },
  {
    icon: Users,
    title: 'Ton accompagnateur',
    items: [
      'Permis B depuis au moins 5 ans sans interruption + accord de son assureur',
      'Présent obligatoirement aux rendez-vous pédagogiques',
      "Son rôle : co-pilote qui anticipe et rassure — pas un juge",
      'Plusieurs accompagnateurs possibles (parents, proches), chacun déclaré',
    ],
  },
  {
    icon: Car,
    title: 'Un véhicule partagé',
    items: [
      'Avant chaque trajet : réglages siège, rétroviseurs, commandes',
      "Habitue-toi aux réactions de la voiture : embrayage, freinage, gabarit",
      "Participe à l'entretien : pression des pneus, niveaux, éclairage",
      "Équipement conseillé : rétroviseur intérieur additionnel + disque « conduite accompagnée »",
    ],
  },
  {
    icon: Route,
    title: 'Des kilomètres et des itinéraires variés',
    items: [
      'Commence par des trajets courts (15-30 min) sur des routes connues, puis allonge progressivement',
      '10 min en ville dense fatiguent autant que 30 min de campagne — dose la difficulté',
      'Varie vite : pluie, nuit, campagne, ville, voie rapide',
      'Note tout dans ton livret : kilomètres, conditions, ressenti',
    ],
  },
  {
    icon: Eye,
    title: 'VORACE + conduite commentée',
    items: [
      'Avant de partir, scanne VORACE : Véhicule · Organisation · Réglementation · Accompagnateur · Conducteur · Environnement',
      'Conduite commentée : dis à voix haute ce que tu vois, prévois et fais — 10 min par trajet',
      "On retient 20 % de ce qu'on entend, 70 % de ce qu'on fait soi-même",
      'Débriefe à l\'arrêt avec ton accompagnateur, jamais en roulant',
    ],
  },
  {
    icon: Gauge,
    title: "Anticiper comme un expert",
    items: [
      'Vois au-delà du visible : piéton caché derrière un camion, portière qui s\'ouvre',
      'Zone d\'incertitude = ralentis tant que tu ne sais pas',
      'Pied au-dessus du frein en zone de danger : temps de réaction divisé par 2, ≈ 7 m gagnés à 50 km/h',
      'Regard loin devant + rétroviseurs toutes les 5 à 10 secondes',
    ],
  },
  {
    icon: ShieldAlert,
    title: 'Les pièges à connaître',
    items: [
      "Le cap des 500 premiers km : l'excès de confiance est ton premier ennemi",
      'La pression des copains : au volant, c\'est toi qui décides — prépare tes réponses à l\'avance',
      "Les facteurs de risque s'additionnent : fatigue + retard + téléphone + pluie",
      'Savoir renoncer ou reporter un trajet est une compétence de conducteur',
    ],
  },
];

export default function GuideAacElevePage() {
  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-4 lg:px-6 mt-8 pb-10" data-testid="guide-aac-page">
        <SectionTitle
          kicker="Conduite accompagnée · AAC"
          title="Le guide de la conduite accompagnée"
          sub="L'essentiel du guide officiel remis lors de ton rendez-vous préalable — à relire avec ton accompagnateur avant et pendant tes 3 000 km."
        />

        {/* Chiffres clés */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-testid="guide-aac-stats">
          {KEY_STATS.map((s) => (
            <div key={s.n} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="font-serif-editorial text-red-400 text-[24px] leading-none">{s.n}</div>
              <div className="text-white/60 text-[12px] mt-2 leading-snug">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {SECTIONS.map((sec) => (
            <div key={sec.title} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6" data-testid={`guide-aac-section-${sec.title.slice(0, 10)}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-9 h-9 rounded-xl bg-red-600/15 border border-red-500/30 text-red-400 inline-flex items-center justify-center"><sec.icon size={16} /></span>
                <h2 className="text-white font-semibold text-base md:text-lg">{sec.title}</h2>
              </div>
              <ul className="space-y-2.5">
                {sec.items.map((it, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/75 text-[13.5px] leading-relaxed">
                    <ArrowRight size={14} className="shrink-0 text-red-500 mt-1" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Citation */}
        <div className="mt-8 rounded-2xl border border-red-500/25 bg-red-950/10 p-6 flex gap-4">
          <Quote size={22} className="text-red-400 shrink-0" />
          <div>
            <p className="text-white/85 text-[15px] leading-relaxed italic">« Au début, j'appuyais sur un frein imaginaire côté passager… Au fil des kilomètres, la confiance s'est installée des deux côtés. »</p>
            <p className="text-white/40 text-[12px] mt-2">Témoignage d'un accompagnateur · Guide de la conduite accompagnée</p>
          </div>
        </div>

        {/* Liens utiles */}
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="/docs/Attestation_assurance_AAC_CS.pdf" download data-testid="guide-aac-download-attestation" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-white text-black hover:bg-white/85 text-[13px] font-semibold">
            <ArrowRight size={14} className="rotate-90" /> Attestation d'assurance AAC/CS (PDF)
          </a>
          <Link to="/cours/eleve/bilans" data-testid="guide-aac-to-bilans" className="inline-flex items-center gap-2 h-10 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold">
            <BookOpen size={14} /> Mes bilans AAC
          </Link>
          <Link to="/simulateurs" className="inline-flex items-center gap-2 h-10 px-5 rounded-full border border-white/15 text-white/70 hover:text-white text-[13px] font-semibold">
            Simulateur d'alcoolémie & distances
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
